import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { trackCompleteRegistration } from '../utils/fbPixel';
import { CheckCircle2, ClipboardList, PhoneCall, MessageSquare, Smartphone, Phone, AlertTriangle, Play, Star, Heart } from 'lucide-react';
import { Footer } from '../components/Footer';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.5
  }
};
export function ThankYou() {
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    // Fire CompleteRegistration only — PageView is handled by PageViewTracker
    const timer = setTimeout(() => {
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'OmniCredit Lead Form'
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  return <div className="min-h-screen bg-dark-primary font-sans text-slate-300 selection:bg-omni/30 selection:text-white flex flex-col" data-id="element-118">
      <main className="flex-1" data-id="element-119">
        {/* 1. HERO — Confirmation (with integrated logo) */}
        <section className="pt-3 pb-16 md:pt-6 md:pb-24 relative overflow-hidden" data-id="element-120">
          <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle, #2DD4A8 0.8px, transparent 0.8px)',
          backgroundSize: '24px 24px'
        }} data-id="element-121"></div>
          <div className="absolute top-[20%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-omni/5 blur-[120px] pointer-events-none" data-id="element-122"></div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-123">
            <img src="https://cdn.magicpatterns.com/uploads/c2J7EfJRgjCnjErKDdmjhc/tmpu_weircg.webp" alt="OmniCredit Logo" className="h-10 md:h-12 object-contain mr-auto mb-6" data-id="element-124" />
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            type: 'spring'
          }} className="max-w-3xl mx-auto text-center" data-id="element-125">
              <div className="relative w-20 h-20 mx-auto mb-8" data-id="element-126">
                {/* Sonar rings */}
                <span className="absolute inset-0 rounded-full bg-omni/20 animate-ping" style={{
                animationDuration: '2s'
              }} data-id="element-127"></span>
                <span className="absolute inset-[-8px] rounded-full bg-omni/10 animate-ping" style={{
                animationDuration: '2s',
                animationDelay: '0.5s'
              }} data-id="element-128"></span>
                {/* Main circle */}
                <div className="relative w-20 h-20 bg-omni/10 rounded-full flex items-center justify-center" data-id="element-129">
                  <CheckCircle2 className="w-10 h-10 text-omni" data-id="element-130" />
                </div>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl text-white mb-4 leading-tight" data-id="element-131">
                <span className="font-light" data-id="element-132">Tu solicitud</span>{' '}
                <span className="font-bold" data-id="element-133">fue recibida correctamente</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-300 mb-3" data-id="element-134">
                Un especialista de OmniCredit está revisando tu información en
                este momento.
              </p>

              <p className="text-base text-omni font-medium mb-4" data-id="element-135">
                El siguiente paso es muy importante para continuar con tu
                proceso.
              </p>

              <p className="text-sm text-slate-400 mb-6" data-id="element-136">
                Para confirmar tu disponibilidad, toca uno de los botones:
              </p>

              {!confirmed ? <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-137">
                  <a href="sms:+17727580352?body=Confirmo%20mi%20disponibilidad%20-%20OmniCredit" onClick={() => setConfirmed(true)} className="flex items-center justify-center gap-3 bg-omni hover:bg-omni-light text-dark-primary font-bold text-base py-4 px-8 rounded-xl shadow-[0_0_20px_rgba(45,212,168,0.3)] hover:shadow-[0_0_30px_rgba(45,212,168,0.5)] transition-all transform hover:-translate-y-1 w-full sm:w-auto" data-id="element-138">
                    <MessageSquare className="w-5 h-5" data-id="element-139" />
                    <span data-id="element-140">Confirmar por Texto</span>
                  </a>
                </div> : <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.4
            }} className="inline-flex items-center gap-2 text-omni font-semibold" data-id="element-141">
                  <CheckCircle2 className="w-5 h-5" data-id="element-142" />
                  <span data-id="element-143">
                    ¡Confirmado! Un especialista te contactará pronto.
                  </span>
                </motion.div>}
            </motion.div>
          </div>
        </section>

        {/* 2. WHAT HAPPENS NEXT — 3 Steps */}
        <section className="py-16 bg-dark-secondary" data-id="element-144">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-145">
            <div className="max-w-4xl mx-auto" data-id="element-146">
              <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-147">
                {/* Connecting line */}
                <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 bg-slate-700 z-0" data-id="element-148"></div>

                {[{
                num: '1',
                icon: ClipboardList,
                title: 'Revisamos tu información',
                desc: 'Nuestro equipo analiza tu caso de forma personalizada.'
              }, {
                num: '2',
                icon: PhoneCall,
                title: 'Un especialista te llamará',
                desc: 'Recibirás una llamada en breve para hablar sobre tu situación.'
              }, {
                num: '3',
                icon: MessageSquare,
                title: 'Te explicamos tus opciones',
                desc: 'Te presentaremos un plan claro con los próximos pasos.'
              }].map((step, i) => {
                const Icon = step.icon;
                return <motion.div key={i} {...fadeUp} transition={{
                  duration: 0.5,
                  delay: i * 0.15
                }} className="flex flex-col items-center text-center relative z-10" data-id="element-149">
                      <div className="w-20 h-20 bg-dark-tertiary rounded-full border-4 border-omni flex items-center justify-center mb-5 shadow-[0_0_15px_rgba(45,212,168,0.2)]" data-id="element-150">
                        <Icon className="w-8 h-8 text-omni" data-id="element-151" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2" data-id="element-152">
                        {step.title}
                      </h3>
                      <p className="text-sm text-slate-400" data-id="element-153">{step.desc}</p>
                    </motion.div>;
              })}
              </div>
            </div>
          </div>
        </section>

        {/* REINFORCE DECISION (moved up) */}
        <section className="py-16 bg-dark-primary" data-id="element-154">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-155">
            <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center" data-id="element-156">
              <Heart className="w-10 h-10 text-omni/40 mx-auto mb-6" data-id="element-157" />
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" data-id="element-158">
                Tomaste una buena decisión
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto" data-id="element-159">
                Muchas personas comienzan su proceso sin tener claridad. Dar
                este paso te permite entender tus opciones y tomar mejores
                decisiones financieras.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 3. EXPECTATION SETTING */}

        {/* 4. BEHAVIOR CONTROL — HIGH IMPACT */}
        <section id="behavior-control" className="py-16 bg-dark-primary relative overflow-hidden" data-id="element-160">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-161">
            <motion.div {...fadeUp} className="max-w-2xl mx-auto bg-dark-tertiary rounded-2xl border-l-4 border-omni p-8 md:p-10 relative overflow-hidden" data-id="element-162">
              <div className="absolute top-0 right-0 w-40 h-40 bg-omni/5 rounded-full blur-[60px] pointer-events-none" data-id="element-163"></div>

              <div className="flex items-center gap-3 mb-6" data-id="element-164">
                <AlertTriangle className="w-6 h-6 text-omni" data-id="element-165" />
                <h2 className="text-xl md:text-2xl font-bold text-white uppercase tracking-wide" data-id="element-166">
                  Importante: No pierdas la llamada
                </h2>
              </div>

              <p className="text-slate-300 mb-6" data-id="element-167">
                Para avanzar con tu solicitud, es clave que puedas contestar la
                llamada de nuestro equipo.
              </p>

              <div className="space-y-3" data-id="element-168">
                {['Mantén tu teléfono cerca', 'Responde números desconocidos', 'Nuestro equipo intentará contactarte lo antes posible'].map((text, i) => <div key={i} className="flex items-center gap-3" data-id="element-169">
                    <div className="w-2 h-2 rounded-full bg-omni flex-shrink-0" data-id="element-170"></div>
                    <span className="text-slate-200 font-medium" data-id="element-171">{text}</span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5. VIDEO SECTION */}

        {/* 6. SOCIAL PROOF */}
        <section className="py-16 bg-dark-primary" data-id="element-172">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-173">
            <motion.div {...fadeUp} className="max-w-4xl mx-auto" data-id="element-174">
              <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10" data-id="element-175">
                Personas que ya dieron el siguiente paso
              </h2>

              <div className="grid md:grid-cols-3 gap-6" data-id="element-176">
                {[{
                name: 'María G.',
                text: 'Contesté la llamada y en menos de 15 minutos ya tenía un plan claro. Muy profesionales.'
              }, {
                name: 'Carlos R.',
                text: 'Al principio dudé, pero el especialista me explicó todo con mucha paciencia. Valió la pena.'
              }, {
                name: 'Ana L.',
                text: 'Gracias a OmniCredit entendí mis opciones reales. El proceso fue más fácil de lo que pensé.'
              }].map((review, i) => <motion.div key={i} {...fadeUp} transition={{
                duration: 0.5,
                delay: i * 0.1
              }} className="bg-dark-secondary p-6 rounded-xl border border-slate-700/50" data-id="element-177">
                    <div className="flex items-center gap-0.5 mb-4" data-id="element-178">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-omni fill-omni" data-id="element-179" />)}
                    </div>
                    <p className="text-slate-300 text-sm mb-4 leading-relaxed" data-id="element-180">
                      "{review.text}"
                    </p>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-181">
                      — {review.name}
                    </span>
                  </motion.div>)}
              </div>

              <div className="flex flex-wrap justify-center gap-8 mt-10 text-sm text-slate-500 font-medium" data-id="element-182">
                <span data-id="element-183">✓ Clientes atendidos en todo el país</span>
                <span data-id="element-184">✓ Equipo especializado en análisis financiero</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 8. PSYCHOLOGICAL CTA */}
        <section className="py-16 bg-dark-secondary" data-id="element-185">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-186">
            <motion.div {...fadeUp} className="max-w-xl mx-auto text-center" data-id="element-187">
              {!confirmed ? <>
                  <Phone className="w-8 h-8 text-omni mx-auto mb-6" data-id="element-188" />
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-8" data-id="element-189">
                    ¿Estás listo para recibir la llamada?
                  </h2>
                  <button onClick={() => setConfirmed(true)} className="bg-omni hover:bg-omni-light text-dark-primary font-bold text-lg py-4 px-10 rounded-xl shadow-[0_0_20px_rgba(45,212,168,0.3)] hover:shadow-[0_0_30px_rgba(45,212,168,0.5)] transition-all transform hover:-translate-y-1" data-id="element-190">
                    Confirmar mi disponibilidad
                  </button>
                </> : <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-191">
                  <CheckCircle2 className="w-10 h-10 text-omni mx-auto mb-4" data-id="element-192" />
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-193">
                    ¡Perfecto!
                  </h3>
                  <p className="text-slate-400" data-id="element-194">
                    ¡Confirmado! Un especialista te contactará pronto.
                  </p>
                </motion.div>}
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-195" />
    </div>;
}